/**
 * Admin — Gestion complète des retraites & séjours spirituels.
 * Onglets : Liste des retraites | Réservations | Liste d'attente
 *
 * Utilise les endpoints /api/admin/retreats* (require_admin).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Settings, X, ArrowLeft, Users, Clock, Sparkles,
  Calendar as CalendarIcon, MapPin, Copy, Archive, Download, Wallet,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { API } from "../../lib/api";
import { ImageUploader } from "../ImageUploader";
import { ClientPicker } from "./ClientPicker";

export function AdminRetreatsManager({ onOpenClient }) {
  const [retreats, setRetreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // retreat en cours d'édition
  const [subTab, setSubTab] = useState("info"); // info | bookings | waitlist
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/retreats`, { withCredentials: true });
      setRetreats(r.data.retreats || []);
    } catch (e) {
      toast.error("Impossible de charger les retraites");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const openEditor = async (r) => {
    // Recharger la retraite complète (route admin renvoie tout)
    try {
      const res = await axios.get(`${API}/admin/retreats/${r.retreat_id}`, { withCredentials: true });
      setEditing(res.data);
      setSubTab("info");
    } catch { toast.error("Erreur chargement retraite"); }
  };

  const createBlank = async () => {
    const title = prompt("Titre de la retraite ? (ex: Retraite au Mas d'Archas)");
    if (!title) return;
    setCreating(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        title,
        subtitle: "",
        start_date: today,
        end_date: today,
        description: "",
        total_capacity: 12,
        day_pass_enabled: true,
        day_pass_price: 0,
        deposit_percent: 30,
        deposit_deadline_days: 45,
        cancel_deadline_days: 30,
        is_published: false,
        room_types: [],
        program: [],
        location: { name: "", city: "", country: "France" },
      };
      const res = await axios.post(`${API}/admin/retreats`, payload, { withCredentials: true });
      await refresh();
      const full = await axios.get(`${API}/admin/retreats/${res.data.retreat_id}`, { withCredentials: true });
      setEditing(full.data);
      setSubTab("info");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setCreating(false); }
  };

  const deleteRetreat = async (r) => {
    if (!confirm(`Supprimer définitivement "${r.title}" ?`)) return;
    const doDelete = async (force) => {
      await axios.delete(`${API}/admin/retreats/${r.retreat_id}${force ? '?force=true' : ''}`, { withCredentials: true });
    };
    try {
      await doDelete(false);
      toast.success("Retraite supprimée");
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (typeof detail === 'string' && detail.startsWith('__HAS_BOOKINGS__')) {
        const [, total, active] = detail.split(':');
        const msg = `⚠️ Ce séjour a ${total} réservation(s)${Number(active) > 0 ? ` dont ${active} ACTIVE(S)` : ''}.\n\n`
          + `La suppression forcée effacera DÉFINITIVEMENT le séjour, toutes ses réservations (y compris payées) et sa liste d'attente. Cette action est irréversible.\n\n`
          + `Confirmer la suppression forcée ?`;
        if (!confirm(msg)) return;
        try {
          await doDelete(true);
          toast.success("Séjour et réservations supprimés");
          refresh();
        } catch (e2) { toast.error(e2.response?.data?.detail || "Erreur lors de la suppression forcée"); }
      } else {
        toast.error(detail || "Erreur");
      }
    }
  };

  const togglePublish = async (r, next) => {
    try {
      await axios.post(
        `${API}/admin/retreats/${r.retreat_id}/publish`,
        { is_published: next },
        { withCredentials: true }
      );
      toast.success(next ? "Retraite publiée" : "Retraite masquée");
      // Mise à jour optimiste locale
      setRetreats((prev) => prev.map((x) => x.retreat_id === r.retreat_id ? { ...x, is_published: next } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const duplicateRetreat = async (r) => {
    if (!confirm(`Dupliquer "${r.title}" ?\n\nLa copie repartira en brouillon (non publiée). Tu pourras ensuite modifier les dates, chambres, programme…`)) return;
    try {
      const res = await axios.post(`${API}/admin/retreats/${r.retreat_id}/duplicate`, {}, { withCredentials: true });
      toast.success("Retraite dupliquée — ouverture de la copie…");
      await refresh();
      // Ouvre directement la copie pour édition rapide
      const full = await axios.get(`${API}/admin/retreats/${res.data.retreat_id}`, { withCredentials: true });
      setEditing(full.data);
      setSubTab("info");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (editing) {
    return (
      <RetreatEditor
        retreat={editing}
        subTab={subTab}
        setSubTab={setSubTab}
        onClose={() => { setEditing(null); refresh(); }}
        onOpenClient={onOpenClient}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium flex items-center gap-2"><Sparkles className="text-[#F5D8A2]" /> Séjours & Retraites</h2>
        <Button onClick={createBlank} disabled={creating} className="bg-[#F5D8A2] text-black hover:bg-[#F5D8A2]/90" data-testid="admin-new-retreat">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle retraite
        </Button>
      </div>

      {loading ? <div className="text-white/50">Chargement…</div> : (() => {
        // Split actives / passées selon end_date.
        // Une retraite est "passée" quand son end_date < aujourd'hui.
        const today = new Date().toISOString().slice(0, 10);
        const active = retreats.filter((r) => (r.end_date || "") >= today);
        const past = retreats.filter((r) => (r.end_date || "") < today);
        return (
          <div className="space-y-6">
            {/* Retraites actives */}
            <section>
              <h3 className="text-sm text-white/60 uppercase tracking-wide mb-3">
                À venir ({active.length})
              </h3>
              <div className="grid gap-3">
                {active.length === 0 && <div className="text-white/50 italic bg-white/5 p-6 rounded-lg">Aucune retraite à venir. Clique sur « Nouvelle retraite » ou duplique-en une passée.</div>}
                {active.map((r) => (
                  <RetreatRow
                    key={r.retreat_id}
                    r={r}
                    onEdit={openEditor}
                    onDelete={deleteRetreat}
                    onDuplicate={duplicateRetreat}
                    onTogglePublish={togglePublish}
                    onOpenClient={onOpenClient}
                  />
                ))}
              </div>
            </section>

            {/* Retraites passées & archivées */}
            {past.length > 0 && (
              <section>
                <h3 className="text-sm text-white/40 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Archive className="w-4 h-4" />
                  Passées & archivées ({past.length})
                </h3>
                <div className="grid gap-3">
                  {past.map((r) => (
                    <RetreatRow
                      key={r.retreat_id}
                      r={r}
                      archived
                      onEdit={openEditor}
                      onDelete={deleteRetreat}
                      onDuplicate={duplicateRetreat}
                      onTogglePublish={togglePublish}
                      onOpenClient={onOpenClient}
                    />
                  ))}
                </div>
                <p className="text-xs text-white/40 mt-2 italic">Les retraites passées sont automatiquement masquées du site public — même si le bouton « En ligne » est activé.</p>
              </section>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/**
 * Une ligne dans la liste des retraites (actives ou archivées).
 * ``archived`` : styling atténué + libellés adaptés.
 */
function RetreatRow({ r, archived, onEdit, onDelete, onDuplicate, onTogglePublish, onOpenClient }) {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null); // { bookings, waitlist, availability }

  const toggleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (details) return; // déjà chargé
    try {
      const [bk, wl, avail] = await Promise.all([
        axios.get(`${API}/admin/retreats/${r.retreat_id}/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/retreats/${r.retreat_id}/waitlist`, { withCredentials: true }),
        axios.get(`${API}/retreats/${r.slug}/availability`).catch(() => ({ data: {} })),
      ]);
      setDetails({
        bookings: bk.data.bookings || [],
        waitlist: wl.data.waitlist || [],
        availability: avail.data || {},
      });
    } catch { setDetails({ bookings: [], waitlist: [], availability: {} }); }
  };

  return (
    <div className={`bg-white/5 border rounded-lg overflow-hidden transition-all ${archived ? "border-white/5 opacity-70" : "border-white/10"}`} data-testid={`admin-retreat-row-${r.slug}`}>
      {/* Ligne cliquable — expand résumé inline */}
      <div className="p-4 flex items-start justify-between gap-4 cursor-pointer hover:bg-white/5" onClick={toggleExpand}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {archived
              ? <Badge className="bg-white/10 text-white/50 border-white/10">Archivée</Badge>
              : !r.is_published
                ? <Badge className="bg-white/10 text-white/70 border-white/20">Brouillon</Badge>
                : <Badge className="bg-green-500/20 text-green-300 border-green-500/30">En ligne</Badge>
            }
            <span className="text-xs text-white/40">/{r.slug}</span>
            <span className="text-xs text-white/30 ml-auto">{expanded ? "▲ replier" : "▼ voir le détail"}</span>
          </div>
          <div className="font-medium">{r.title}</div>
          <div className="text-xs text-white/50 mt-1 flex gap-4 flex-wrap">
            <span><CalendarIcon className="w-3 h-3 inline mr-1" />{r.start_date} → {r.end_date}</span>
            {r.location?.name && <span><MapPin className="w-3 h-3 inline mr-1" />{r.location.name}</span>}
            <span><Users className="w-3 h-3 inline mr-1" />{r.bookings_count || 0} résa · {r.waitlist_count || 0} en attente</span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
          {!archived && (
            <label className="flex items-center gap-2 cursor-pointer" title={r.is_published ? "Cliquez pour masquer" : "Cliquez pour publier"}>
              <span className="text-xs text-white/50">{r.is_published ? "En ligne" : "Masquée"}</span>
              <Switch checked={!!r.is_published} onCheckedChange={(v) => onTogglePublish(r, v)} data-testid={`admin-publish-toggle-${r.slug}`} />
            </label>
          )}
          <Button size="sm" variant="ghost" className="text-[#F5D8A2]" onClick={() => onDuplicate(r)} title="Dupliquer" data-testid={`admin-duplicate-${r.slug}`}><Copy className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(r)} title="Éditer complet" data-testid={`admin-edit-${r.slug}`}><Settings className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDelete(r)} data-testid={`admin-delete-${r.slug}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Panneau détail inline */}
      {expanded && (
        <div className="border-t border-white/10 bg-black/20 p-4 text-sm" data-testid={`retreat-summary-${r.slug}`}>
          {!details ? (
            <div className="text-white/50">Chargement…</div>
          ) : (
            <div className="space-y-4">
              {/* Places restantes */}
              {details.availability?.total_capacity ? (
                <div className="flex gap-6 text-xs text-white/70 flex-wrap">
                  <span>👥 Places totales : <strong className="text-white">{details.availability.total_capacity}</strong></span>
                  <span>🛏️ Lits pris : <strong className="text-white">{details.availability.beds_booked}</strong></span>
                  <span>☀️ Externes Day : <strong className="text-white">{details.availability.day_pax_booked}</strong></span>
                  <span className="text-[#F5D8A2]">Restant : <strong>{details.availability.spots_remaining}</strong></span>
                </div>
              ) : null}

              {/* Réservations */}
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wide mb-2">Inscrit·es ({details.bookings.length})</div>
                {details.bookings.length === 0 ? (
                  <div className="text-white/40 italic text-xs">Aucune réservation pour le moment.</div>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {details.bookings.map((b) => (
                      <div key={b.booking_id} className="flex items-center gap-3 py-1.5 px-2 bg-white/5 rounded text-xs">
                        {onOpenClient && b.user_id ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpenClient(b.user_id); }}
                            className="font-medium text-white flex-1 truncate text-left hover:text-[#F5D8A2] hover:underline transition-colors"
                            title="Voir la fiche client"
                            data-testid={`retreat-participant-${b.booking_id}`}
                          >
                            {b.first_name} {b.last_name}
                          </button>
                        ) : (
                          <span className="font-medium text-white flex-1 truncate">{b.first_name} {b.last_name}</span>
                        )}
                        <span className="text-white/50 truncate hidden sm:inline">{b.email}</span>
                        {b.phone && (
                          <a
                            href={`tel:${b.phone.replace(/\s/g, "")}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[#00E5FF] hover:underline shrink-0 hidden md:inline"
                            title="Appeler"
                          >
                            📞 {b.phone}
                          </a>
                        )}
                        {b.admin_room_note && (
                          <span className="text-[10px] text-[#F5D8A2] bg-[#F5D8A2]/10 border border-[#F5D8A2]/30 rounded px-1.5 py-0.5 shrink-0" title="Chambre attribuée (admin)">
                            🔑 {b.admin_room_note}
                          </span>
                        )}
                        <span className="text-white/70 shrink-0">
                          {b.booking_type === "day" ? `Day×${b.day_pax}` : `${b.occupancy === "double" ? "Dbl" : "Sgl"}`}
                        </span>
                        <span className="text-[#F5D8A2] shrink-0">{b.amount_paid || 0}€/{b.total_amount}€</span>
                        <Badge className={`text-[9px] shrink-0 ${b.status === "paid" ? "bg-green-500/20 text-green-300" : b.status === "cancelled" ? "bg-red-500/20 text-red-300" : b.status === "balance_due" ? "bg-orange-500/20 text-orange-300" : "bg-blue-500/20 text-blue-300"}`}>{b.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Liste d'attente */}
              {details.waitlist.length > 0 && (
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wide mb-2">Liste d'attente ({details.waitlist.length})</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {details.waitlist.map((w, i) => (
                      <div key={w.waitlist_id} className="flex items-center gap-3 py-1.5 px-2 bg-white/5 rounded text-xs">
                        <span className="text-white/60">#{i + 1}</span>
                        <span className="font-medium text-white flex-1 truncate">{w.first_name} {w.last_name}</span>
                        <span className="text-white/50 truncate hidden sm:inline">{w.email}</span>
                        <span className="text-white/40 shrink-0">{new Date(w.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-white/10 flex justify-end">
                <Button size="sm" variant="outline" onClick={() => onEdit(r)} className="border-[#F5D8A2]/30 text-[#F5D8A2] hover:bg-[#F5D8A2]/10 text-xs">
                  <Settings className="w-3 h-3 mr-1" /> Ouvrir l'éditeur complet
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RetreatEditor({ retreat, subTab, setSubTab, onClose, onOpenClient }) {
  const [data, setData] = useState(retreat);
  const [saving, setSaving] = useState(false);

  const patch = (upd) => setData({ ...data, ...upd });
  const patchLoc = (upd) => setData({ ...data, location: { ...(data.location || {}), ...upd } });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/retreats/${data.retreat_id}`, data, { withCredentials: true });
      setData(res.data);
      toast.success("Retraite enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  // Rooms helpers
  const addRoom = () => patch({ room_types: [...(data.room_types || []), { name: "Nouvelle chambre", stock: 1, price_single: 559, price_double: 999, allow_single: true, allow_double: true, beds: 2 }] });
  const updateRoom = (idx, upd) => {
    const rooms = [...(data.room_types || [])];
    rooms[idx] = { ...rooms[idx], ...upd };
    patch({ room_types: rooms });
  };
  const removeRoom = (idx) => patch({ room_types: (data.room_types || []).filter((_, i) => i !== idx) });

  // Program helpers
  const addDay = () => patch({ program: [...(data.program || []), { day_index: (data.program?.length || 0) + 1, title: "", description: "" }] });
  const updateDay = (idx, upd) => {
    const p = [...(data.program || [])];
    p[idx] = { ...p[idx], ...upd };
    patch({ program: p });
  };
  const removeDay = (idx) => patch({ program: (data.program || []).filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onClose}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
          <h2 className="text-xl font-medium">{data.title || "Retraite"}</h2>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#F5D8A2] text-black hover:bg-[#F5D8A2]/90" data-testid="admin-save-retreat">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-white/10 flex-wrap">
        {[
          { id: "info", label: "Infos & programme" },
          { id: "rooms", label: "Chambres & tarifs" },
          { id: "bookings", label: "Réservations" },
          { id: "waitlist", label: "Liste d'attente" },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`admin-subtab-${t.id}`}
            className={`px-4 py-2 text-sm border-b-2 transition ${subTab === t.id ? "border-[#F5D8A2] text-[#F5D8A2]" : "border-transparent text-white/60 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 py-1">
          <ExportCsvButton retreatId={data.retreat_id} slug={data.slug} />
          <BrevoSyncButton retreatId={data.retreat_id} brevoListId={data.brevo_list_id} />
        </div>
      </div>

      {subTab === "info" && (
        <div className="space-y-6 bg-white/5 border border-white/10 rounded-lg p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-white/60 block mb-1">Titre</label>
              <Input value={data.title || ""} onChange={(e) => patch({ title: e.target.value })} data-testid="ret-title" />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">Slug (URL)</label>
              <Input value={data.slug || ""} onChange={(e) => patch({ slug: e.target.value })} data-testid="ret-slug" />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">Accroche</label>
            <Input value={data.subtitle || ""} onChange={(e) => patch({ subtitle: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-white/60 block mb-1">Date début</label>
              <Input type="date" value={data.start_date || ""} onChange={(e) => patch({ start_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">Date fin</label>
              <Input type="date" value={data.end_date || ""} onChange={(e) => patch({ end_date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">Description</label>
            <Textarea rows={4} value={data.description || ""} onChange={(e) => patch({ description: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">Photo de couverture</label>
            <ImageUploader currentUrl={data.cover_image} onUploaded={(url) => patch({ cover_image: url })} />
          </div>

          {/* Galerie multi-photos — nombre illimité, drag & drop pour réorganiser,
              description facultative par photo pour SEO / accessibilité. */}
          <div>
            <label className="text-xs text-white/60 block mb-2">Galerie photos ({(data.gallery || []).length})</label>
            <p className="text-xs text-white/40 mb-3">
              Glisse-dépose pour réorganiser l'ordre. Ajoute une description facultative sous chaque photo (utile pour l'accessibilité et le SEO).
            </p>
            <GalleryEditor
              items={data.gallery || []}
              onChange={(next) => patch({ gallery: next })}
            />
          </div>

          <div className="pt-4 border-t border-white/10 space-y-3">
            <h3 className="text-sm font-medium text-[#F5D8A2]">Lieu</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input placeholder="Nom" value={data.location?.name || ""} onChange={(e) => patchLoc({ name: e.target.value })} />
              <Input placeholder="Ville" value={data.location?.city || ""} onChange={(e) => patchLoc({ city: e.target.value })} />
            </div>
            <Input placeholder="Adresse" value={data.location?.address || ""} onChange={(e) => patchLoc({ address: e.target.value })} />
            <Textarea rows={3} placeholder="Description du lieu" value={data.location?.description || ""} onChange={(e) => patchLoc({ description: e.target.value })} />
          </div>

          <div className="pt-4 border-t border-white/10">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-[#F5D8A2]">Programme jour par jour</h3>
              <Button size="sm" onClick={addDay} variant="outline"><Plus className="w-3 h-3 mr-1" /> Ajouter</Button>
            </div>
            <div className="space-y-3">
              {(data.program || []).map((p, idx) => (
                <div key={idx} className="border border-white/10 rounded p-3 space-y-2">
                  <div className="flex justify-between gap-2">
                    <Input type="number" min={1} value={p.day_index || 1} onChange={(e) => updateDay(idx, { day_index: parseInt(e.target.value || "1") })} className="w-24" />
                    <Input placeholder="Titre du jour" value={p.title || ""} onChange={(e) => updateDay(idx, { title: e.target.value })} className="flex-1" />
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeDay(idx)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <Textarea rows={2} placeholder="Description" value={p.description || ""} onChange={(e) => updateDay(idx, { description: e.target.value })} />
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-white/10">
            <h3 className="text-sm font-medium text-[#F5D8A2] mb-3">Paramètres</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-white/60 block mb-1">Capacité totale</label>
                <Input type="number" value={data.total_capacity || 12} onChange={(e) => patch({ total_capacity: parseInt(e.target.value || "0") })} />
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Acompte %</label>
                <Input type="number" value={data.deposit_percent || 30} onChange={(e) => patch({ deposit_percent: parseInt(e.target.value || "30") })} />
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Publier</label>
                <div className="flex items-center gap-2 h-9">
                  <Switch
                    checked={!!data.is_published}
                    onCheckedChange={(v) => patch({ is_published: v })}
                    data-testid="ret-published"
                  />
                  <span className="text-sm">{data.is_published ? "En ligne" : "Brouillon"}</span>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-3">
              <label className="text-xs text-white/60 flex items-center gap-2 col-span-1">
                <input type="checkbox" checked={!!data.day_pass_enabled} onChange={(e) => patch({ day_pass_enabled: e.target.checked })} />
                Activer formule Day
              </label>
              <div>
                <label className="text-xs text-white/60 block mb-1">Prix Day (par pers.)</label>
                <Input type="number" step="0.01" value={data.day_pass_price || 0} onChange={(e) => patch({ day_pass_price: parseFloat(e.target.value || "0") })} />
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Meta description SEO</label>
                <Input value={data.meta_description || ""} onChange={(e) => patch({ meta_description: e.target.value })} />
              </div>
            </div>
          </div>
        </div>
      )}

      {subTab === "rooms" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={addRoom} className="bg-[#F5D8A2] text-black hover:bg-[#F5D8A2]/90"><Plus className="w-4 h-4 mr-1" /> Ajouter une chambre</Button>
          </div>
          {(data.room_types || []).map((rt, idx) => (
            <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3" data-testid={`admin-room-${idx}`}>
              <div className="flex justify-between gap-3">
                <Input value={rt.name || ""} onChange={(e) => updateRoom(idx, { name: e.target.value })} placeholder="Nom de la chambre" className="flex-1" />
                <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeRoom(idx)}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <Textarea rows={2} placeholder="Description" value={rt.description || ""} onChange={(e) => updateRoom(idx, { description: e.target.value })} />
              <div className="grid sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-white/60 block mb-1">Stock</label>
                  <Input type="number" value={rt.stock || 0} onChange={(e) => updateRoom(idx, { stock: parseInt(e.target.value || "0") })} />
                </div>
                <div>
                  <label className="text-xs text-white/60 block mb-1">Prix simple (1 pers)</label>
                  <Input type="number" step="0.01" value={rt.price_single || 0} onChange={(e) => updateRoom(idx, { price_single: parseFloat(e.target.value || "0") })} />
                </div>
                <div>
                  <label className="text-xs text-white/60 block mb-1">Prix double (2 pers)</label>
                  <Input type="number" step="0.01" value={rt.price_double || 0} onChange={(e) => updateRoom(idx, { price_double: parseFloat(e.target.value || "0") })} />
                </div>
                <div>
                  <label className="text-xs text-white/60 block mb-1">Options</label>
                  <div className="flex gap-3 text-xs mt-2">
                    <label><input type="checkbox" checked={rt.allow_single !== false} onChange={(e) => updateRoom(idx, { allow_single: e.target.checked })} /> Simple</label>
                    <label><input type="checkbox" checked={rt.allow_double !== false} onChange={(e) => updateRoom(idx, { allow_double: e.target.checked })} /> Double</label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {subTab === "bookings" && <BookingsList retreat={data} onOpenClient={onOpenClient} />}
      {subTab === "waitlist" && <WaitlistList retreatId={data.retreat_id} />}
    </div>
  );
}

function BookingsList({ retreat, onOpenClient }) {
  const retreatId = retreat?.retreat_id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/retreats/${retreatId}/bookings`, { withCredentials: true });
      setItems(r.data.bookings || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [retreatId]);

  const updateStatus = async (booking_id, status) => {
    // Confirmation explicite + collecte du motif d'annulation, qui sera inclus dans
    // l'email envoyé au client. L'admin peut aussi renseigner un montant de
    // remboursement (informatif — le remboursement Stripe reste manuel).
    let payload = { status };
    if (status === "cancelled") {
      const b = items.find((x) => x.booking_id === booking_id);
      const who = b ? `${b.first_name} ${b.last_name} (${b.email})` : "cette réservation";
      if (!confirm(`Annuler la réservation de ${who} ?\n\nLa place sera libérée. Un email va être envoyé automatiquement au client pour l'informer.`)) {
        return;
      }
      const reason = prompt(
        "Motif communiqué au client (facultatif, sera inclus dans l'email) :",
        ""
      );
      if (reason === null) return; // l'admin a cliqué "Annuler" sur le prompt
      const refundStr = prompt(
        `Montant à rembourser (€) — facultatif.\n(Le remboursement Stripe est à faire manuellement depuis le dashboard Stripe. Ce champ est purement informatif dans l'email.)`,
        ""
      );
      if (refundStr === null) return;
      payload = {
        status: "cancelled",
        cancellation_reason: (reason || "").trim(),
        refund_amount: refundStr && !isNaN(Number(refundStr)) ? Number(refundStr) : 0,
      };
    }
    try {
      await axios.put(`${API}/admin/retreats/bookings/${booking_id}`, payload, { withCredentials: true });
      toast.success(status === "cancelled" ? "Réservation annulée · email envoyé au client" : "Statut mis à jour");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const saveRoomNote = async (booking_id, admin_room_note) => {
    try {
      await axios.put(
        `${API}/admin/retreats/bookings/${booking_id}`,
        { admin_room_note },
        { withCredentials: true }
      );
      // Optimistic — pas de refresh complet pour ne pas perdre le focus.
      setItems((prev) => prev.map((x) => x.booking_id === booking_id ? { ...x, admin_room_note: (admin_room_note || "").trim() || null } : x));
      toast.success("Chambre attribuée enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  /**
   * Télécharge le PDF "Bon d'inscription" — sert de reçu + preuve d'acceptation
   * CGV pour un paiement en boutique (espèces / virement / offert / autre).
   * Utilise blob + withCredentials pour préserver la session admin.
   */
  const downloadReceipt = async (booking_id, last_name) => {
    try {
      const res = await axios.get(
        `${API}/admin/retreats/bookings/${booking_id}/receipt.pdf`,
        { withCredentials: true, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bon-inscription-${(last_name || "").toLowerCase().replace(/\s/g, "-")}-${booking_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Bon d'inscription téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur téléchargement PDF");
    }
  };

  if (loading) return <div className="text-white/50">Chargement…</div>;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="text-xs text-white/50">
          {items.length} réservation{items.length > 1 ? "s" : ""} au total
        </div>
        <Button
          size="sm"
          onClick={() => setManualOpen(true)}
          className="bg-[#F5D8A2] text-black hover:bg-[#F5D8A2]/90"
          data-testid="admin-manual-booking-open"
        >
          <Plus className="w-4 h-4 mr-1" /> Nouvelle réservation manuelle
        </Button>
      </div>

      {!items.length ? (
        <div className="text-white/50 italic bg-white/5 p-6 rounded-lg">Aucune réservation. Utilise le bouton ci-dessus pour enregistrer un paiement en boutique.</div>
      ) : items.map(b => (
        <div key={b.booking_id} className="bg-white/5 border border-white/10 rounded p-4 text-sm" data-testid={`admin-booking-${b.booking_id}`}>
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium">
                {onOpenClient && b.user_id ? (
                  <button
                    type="button"
                    onClick={() => onOpenClient(b.user_id)}
                    className="text-white hover:text-[#F5D8A2] hover:underline transition-colors"
                    title="Voir la fiche client"
                    data-testid={`booking-open-client-${b.booking_id}`}
                  >
                    {b.first_name} {b.last_name}
                  </button>
                ) : (
                  <span>{b.first_name} {b.last_name}</span>
                )}
                <span className="text-white/50 font-normal"> — {b.email}</span>
                {b.phone && (
                  <>
                    <span className="text-white/50 font-normal"> · </span>
                    <a
                      href={`tel:${b.phone.replace(/\s/g, "")}`}
                      className="text-[#00E5FF] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`booking-phone-${b.booking_id}`}
                    >
                      {b.phone}
                    </a>
                  </>
                )}
              </div>
              <div className="text-xs text-white/50 mt-1">
                {b.booking_type === "day" ? `Day × ${b.day_pax}` : `Chambre — ${b.occupancy === "double" ? "Double" : "Simple"}`}
                {b.companion && ` · avec ${b.companion.first_name} ${b.companion.last_name}`}
              </div>
              {b.day_participants && b.day_participants.length > 0 && (
                <div className="text-xs text-white/50 mt-1">
                  Participants : {b.day_participants.map(p => `${p.first_name} ${p.last_name}`).join(", ")}
                </div>
              )}
              {b.dietary_restrictions && (
                <div className="text-xs text-orange-300 mt-1 bg-orange-500/10 border border-orange-500/20 rounded px-2 py-1 inline-block">
                  ⚠️ {b.dietary_restrictions}
                </div>
              )}
              <div className="text-xs text-white/50 mt-1 flex items-center gap-2 flex-wrap">
                <span>Total : {b.total_amount}€ · Payé : {b.amount_paid}€ · Créé : {new Date(b.created_at).toLocaleDateString("fr-FR")}</span>
                {b.payment_method && b.payment_method !== "stripe" && (
                  <Badge className="bg-[#F5D8A2]/20 text-[#F5D8A2] border-[#F5D8A2]/30 text-[9px]" data-testid={`booking-pm-${b.booking_id}`}>
                    <Wallet className="w-3 h-3 mr-1" />
                    {b.payment_method === "cash" ? "Espèces boutique" : b.payment_method === "gift" ? "Offert" : b.payment_method === "transfer" ? "Virement" : b.payment_method}
                  </Badge>
                )}
                {/* Bon d'inscription PDF — dispo pour toute résa payée hors Stripe.
                    Utile pour compta boutique + preuve papier signable. */}
                {b.payment_method && b.payment_method !== "stripe" && (
                  <button
                    type="button"
                    onClick={() => downloadReceipt(b.booking_id, b.last_name)}
                    className="text-[10px] text-[#F5D8A2] underline hover:no-underline flex items-center gap-1"
                    title="Télécharger le bon d'inscription PDF signable"
                    data-testid={`booking-receipt-${b.booking_id}`}
                  >
                    <Download className="w-3 h-3" /> Bon d'inscription PDF
                  </button>
                )}
              </div>
              {b.admin_notes && (
                <div className="text-xs text-white/40 italic mt-1">📝 {b.admin_notes}</div>
              )}
              <RoomNoteField
                bookingId={b.booking_id}
                initialValue={b.admin_room_note || ""}
                onSave={saveRoomNote}
              />
            </div>
            <div className="text-right shrink-0">
              <Badge className={`${b.status === "paid" ? "bg-green-500/20 text-green-300" : b.status === "cancelled" ? "bg-red-500/20 text-red-300" : "bg-blue-500/20 text-blue-300"} border-white/20 mb-2`}>
                {b.status}
              </Badge>
              <div className="flex gap-1 justify-end">
                {b.status !== "cancelled" && <Button size="sm" variant="ghost" className="text-red-400 text-xs" onClick={() => updateStatus(b.booking_id, "cancelled")}>Annuler</Button>}
                {b.status !== "paid" && <Button size="sm" variant="ghost" className="text-green-400 text-xs" onClick={() => updateStatus(b.booking_id, "paid")}>Marquer payé</Button>}
              </div>
            </div>
          </div>
        </div>
      ))}

      {manualOpen && (
        <ManualBookingModal
          retreat={retreat}
          onClose={() => setManualOpen(false)}
          onCreated={() => { setManualOpen(false); refresh(); }}
        />
      )}
    </div>
  );
}

/**
 * Petit champ inline pour attribuer une chambre à une résa (admin only, non visible côté client).
 * Sauvegarde au blur ou sur Entrée si la valeur a changé.
 */
function RoomNoteField({ bookingId, initialValue, onSave }) {
  const [value, setValue] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(initialValue || ""); }, [initialValue, bookingId]);

  const commit = async () => {
    const trimmed = value.trim();
    if (trimmed === (initialValue || "").trim()) return;
    setSaving(true);
    try { await onSave(bookingId, trimmed); } finally { setSaving(false); }
  };

  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <label className="text-[11px] text-white/50" htmlFor={`room-note-${bookingId}`}>
        🔑 Chambre attribuée <span className="italic text-white/30">(admin uniquement)</span>
      </label>
      <input
        id={`room-note-${bookingId}`}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
        placeholder="Ex: Chambre 3 — Lavande"
        disabled={saving}
        className="text-xs bg-white/5 border border-[#F5D8A2]/30 rounded px-2 py-1 text-white placeholder-white/30 focus:outline-none focus:border-[#F5D8A2] w-64 max-w-full disabled:opacity-50"
        data-testid={`booking-room-note-${bookingId}`}
      />
      {saving && <span className="text-[10px] text-white/40">…</span>}
    </div>
  );
}

function WaitlistList({ retreatId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/admin/retreats/${retreatId}/waitlist`, { withCredentials: true })
      .then(r => setItems(r.data.waitlist || []))
      .finally(() => setLoading(false));
  }, [retreatId]);

  if (loading) return <div className="text-white/50">Chargement…</div>;
  if (!items.length) return <div className="text-white/50 italic bg-white/5 p-6 rounded-lg">Personne sur la liste d'attente.</div>;
  return (
    <div className="space-y-2">
      {items.map((w, i) => (
        <div key={w.waitlist_id} className="bg-white/5 border border-white/10 rounded p-3 flex justify-between text-sm">
          <div>
            <div className="font-medium">#{i + 1} — {w.first_name} {w.last_name}</div>
            <div className="text-xs text-white/50">{w.email} · {w.phone}</div>
            {w.notes && <div className="text-xs text-white/40 mt-1 italic">{w.notes}</div>}
          </div>
          <div className="text-right text-xs">
            <Badge className="bg-white/10 text-white/70 border-white/20">{w.status}</Badge>
            <div className="text-white/40 mt-1">{new Date(w.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Petit bouton "Sync Brevo" — force la (re)création de la liste Brevo dédiée
 * à cette retraite et y pousse tous les emails de participants confirmés.
 * Utile après import CSV ou webhook Stripe manqué. Affiche le list_id si connu.
 */
function BrevoSyncButton({ retreatId, brevoListId }) {
  const [busy, setBusy] = React.useState(false);
  const sync = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/retreats/${retreatId}/sync-brevo`, {}, { withCredentials: true });
      const { synced, total_bookings, brevo_list_id } = r.data;
      toast.success(`Brevo : ${synced}/${total_bookings} pushés · list_id=${brevo_list_id || "n/a"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sync Brevo");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={sync}
      disabled={busy}
      className="text-xs px-3 py-1.5 rounded border border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-50"
      title={brevoListId ? `Liste Brevo #${brevoListId}` : "Créer la liste Brevo et pousser les participants"}
      data-testid="admin-sync-brevo"
    >
      {busy ? "Sync…" : `Sync Brevo${brevoListId ? ` (#${brevoListId})` : ""}`}
    </button>
  );
}

/**
 * Bouton "Export CSV" — télécharge la liste des participants (avec chambre
 * attribuée, téléphone, notes admin) au format CSV compatible Excel FR.
 * Le backend renvoie un flux avec Content-Disposition attachment,
 * mais on utilise fetch + blob pour préserver les cookies withCredentials.
 */
function ExportCsvButton({ retreatId, slug }) {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/retreats/${retreatId}/bookings.csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `participants-${slug || retreatId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur export CSV");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      className="text-xs px-3 py-1.5 rounded border border-[#F5D8A2]/30 text-[#F5D8A2] hover:bg-[#F5D8A2]/10 disabled:opacity-50 flex items-center gap-1"
      title="Télécharger la liste des participants (CSV compatible Excel)"
      data-testid="admin-export-csv"
    >
      <Download className="w-3.5 h-3.5" />
      {busy ? "Export…" : "Export CSV"}
    </button>
  );
}

/**
 * Modale "Nouvelle réservation manuelle" — permet à l'admin d'inscrire un
 * client existant qui a payé en espèces à la boutique. Aucun paiement Stripe
 * n'est déclenché ; la réservation est marquée `paid` par défaut.
 */
function ManualBookingModal({ retreat, onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bookingType, setBookingType] = useState("room");
  const roomTypes = retreat?.room_types || [];
  const [roomTypeId, setRoomTypeId] = useState(roomTypes[0]?.room_type_id || "");
  const [occupancy, setOccupancy] = useState("single");
  const [companionFirst, setCompanionFirst] = useState("");
  const [companionLast, setCompanionLast] = useState("");
  const [dayPax, setDayPax] = useState(1);
  const [dietary, setDietary] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amountPaidStr, setAmountPaidStr] = useState("");
  const [totalOverrideStr, setTotalOverrideStr] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [adminRoomNote, setAdminRoomNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/clients`, { withCredentials: true })
      .then((r) => setClients(r.data || []))
      .catch(() => setClients([]));
  }, []);

  // Prix calculé côté client (visuel — le backend recalcule au submit).
  const computedTotal = React.useMemo(() => {
    if (bookingType === "day") {
      return Number(retreat?.day_pass_price || 0) * Math.max(1, Number(dayPax) || 1);
    }
    const rt = roomTypes.find((x) => x.room_type_id === roomTypeId);
    if (!rt) return 0;
    if (occupancy === "double") return Number(rt.price_double || 0);
    return Number(rt.price_single || 0);
  }, [bookingType, dayPax, occupancy, roomTypeId, roomTypes, retreat]);

  const finalTotal = totalOverrideStr !== "" ? Number(totalOverrideStr) : computedTotal;
  const defaultPaid = finalTotal;

  const submit = async () => {
    if (!selected) { toast.error("Choisis un client"); return; }
    if (bookingType === "room" && !roomTypeId) { toast.error("Choisis une chambre"); return; }
    if (bookingType === "room" && occupancy === "double" && (!companionFirst.trim() || !companionLast.trim())) {
      toast.error("Nom et prénom de l'accompagnant·e requis"); return;
    }

    const payload = {
      user_id: selected.user_id,
      booking_type: bookingType,
      room_type_id: bookingType === "room" ? roomTypeId : null,
      occupancy: bookingType === "room" ? occupancy : "single",
      day_pax: bookingType === "day" ? Math.max(1, Number(dayPax) || 1) : 1,
      day_participants: bookingType === "day"
        ? Array.from({ length: Math.max(1, Number(dayPax) || 1) }).map((_, i) => ({
            first_name: i === 0 ? (selected.first_name || (selected.name || "").split(" ")[0] || "") : "",
            last_name: i === 0 ? (selected.last_name || (selected.name || "").split(" ").slice(1).join(" ") || "") : "",
          }))
        : [],
      companion_first_name: bookingType === "room" && occupancy === "double" ? companionFirst.trim() : "",
      companion_last_name: bookingType === "room" && occupancy === "double" ? companionLast.trim() : "",
      dietary_restrictions: dietary.trim(),
      payment_method: paymentMethod,
      amount_paid: amountPaidStr === "" ? defaultPaid : Number(amountPaidStr),
      total_override: totalOverrideStr === "" ? null : Number(totalOverrideStr),
      admin_notes: adminNotes.trim(),
      admin_room_note: adminRoomNote.trim(),
    };

    // Guard : "day" implique participants nommés → l'API en gère le fallback pour 1 pax.
    if (bookingType === "day" && Number(dayPax) > 1) {
      toast.info("Pour Day > 1 personne, édite les participants après création (feature à venir).");
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/retreats/${retreat.retreat_id}/manual-booking`, payload, { withCredentials: true });
      toast.success("Réservation enregistrée ✨");
      // Auto-ouvre le bon d'inscription dans un nouvel onglet — l'admin peut
      // l'imprimer immédiatement au comptoir de la boutique pour signature.
      const bookingId = res.data?.booking_id;
      if (bookingId && paymentMethod !== "stripe") {
        try {
          const pdfRes = await axios.get(
            `${API}/admin/retreats/bookings/${bookingId}/receipt.pdf`,
            { withCredentials: true, responseType: "blob" }
          );
          const blob = new Blob([pdfRes.data], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          // Petite tempo pour que le nouvel onglet ait chargé le blob avant révoc.
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (_) { /* silencieux — pas bloquant */ }
      }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur — vérifie disponibilité et champs");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="manual-booking-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#1F1533] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#1F1533]/95 backdrop-blur-sm p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-serif text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#F5D8A2]" /> Nouvelle réservation manuelle
            </h3>
            <p className="text-xs text-white/50 mt-1">Enregistrer un client qui a payé en boutique / espèces</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="manual-booking-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Client */}
          <ClientPicker
            clients={clients}
            selected={selected}
            onSelect={setSelected}
            placeholder="Rechercher le client par nom, email ou téléphone…"
            dataTestidPrefix="manual-booking-client"
          />

          {/* Type de formule */}
          <div>
            <label className="text-xs text-white/60 block mb-2">Formule</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setBookingType("room")}
                className={`px-4 py-2 text-sm rounded border transition ${bookingType === "room" ? "bg-[#F5D8A2] text-black border-[#F5D8A2]" : "bg-white/5 border-white/10 text-white/70 hover:border-white/30"}`}
                data-testid="manual-type-room"
              >
                🛏 Immersion (hébergement)
              </button>
              <button
                type="button"
                onClick={() => setBookingType("day")}
                disabled={!retreat?.day_pass_enabled}
                className={`px-4 py-2 text-sm rounded border transition disabled:opacity-40 ${bookingType === "day" ? "bg-[#F5D8A2] text-black border-[#F5D8A2]" : "bg-white/5 border-white/10 text-white/70 hover:border-white/30"}`}
                data-testid="manual-type-day"
              >
                ☀️ Day (sans hébergement)
              </button>
            </div>
          </div>

          {/* Détails formule */}
          {bookingType === "room" ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-white/60 block mb-1">Chambre</label>
                <select
                  value={roomTypeId}
                  onChange={(e) => setRoomTypeId(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white"
                  data-testid="manual-room-type"
                >
                  <option value="">— Sélectionner —</option>
                  {roomTypes.map((rt) => (
                    <option key={rt.room_type_id} value={rt.room_type_id}>
                      {rt.name} · stock {rt.stock} · {rt.price_single}€ simple / {rt.price_double}€ double
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Occupation</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setOccupancy("single")} className={`px-3 py-1.5 text-sm rounded border ${occupancy === "single" ? "bg-white/10 border-white/30" : "border-white/10 text-white/60"}`}>Simple</button>
                  <button type="button" onClick={() => setOccupancy("double")} className={`px-3 py-1.5 text-sm rounded border ${occupancy === "double" ? "bg-white/10 border-white/30" : "border-white/10 text-white/60"}`}>Double</button>
                </div>
              </div>
              {occupancy === "double" && (
                <div className="grid grid-cols-2 gap-3 bg-white/5 border border-white/10 rounded p-3">
                  <div>
                    <label className="text-xs text-white/60 block mb-1">Accompagnant·e — Prénom</label>
                    <Input value={companionFirst} onChange={(e) => setCompanionFirst(e.target.value)} data-testid="manual-companion-first" />
                  </div>
                  <div>
                    <label className="text-xs text-white/60 block mb-1">Accompagnant·e — Nom</label>
                    <Input value={companionLast} onChange={(e) => setCompanionLast(e.target.value)} data-testid="manual-companion-last" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-xs text-white/60 block mb-1">Nombre de participants Day</label>
              <Input type="number" min={1} max={retreat?.total_capacity || 12} value={dayPax} onChange={(e) => setDayPax(Number(e.target.value) || 1)} className="w-32" data-testid="manual-day-pax" />
              <p className="text-[11px] text-white/40 mt-1">Prix unitaire : {retreat?.day_pass_price || 0}€</p>
            </div>
          )}

          {/* Divers */}
          <div>
            <label className="text-xs text-white/60 block mb-1">Régime alimentaire / allergies</label>
            <Input value={dietary} onChange={(e) => setDietary(e.target.value)} placeholder="Ex: végétarien, sans gluten…" />
          </div>

          {/* Paiement */}
          <div className="bg-[#F5D8A2]/5 border border-[#F5D8A2]/20 rounded p-4 space-y-3">
            <div className="text-xs text-[#F5D8A2] uppercase tracking-widest font-medium flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Paiement
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/60 block mb-1">Mode</label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="manual-payment-method">
                  <option value="cash">Espèces (boutique)</option>
                  <option value="transfer">Virement</option>
                  <option value="gift">Offert (cadeau / bon)</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Total (auto = {computedTotal.toFixed(2)}€)</label>
                <Input type="number" step="0.01" min={0} value={totalOverrideStr} onChange={(e) => setTotalOverrideStr(e.target.value)} placeholder={computedTotal.toFixed(2)} data-testid="manual-total-override" />
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Montant payé (défaut = {defaultPaid.toFixed(2)}€)</label>
                <Input type="number" step="0.01" min={0} value={amountPaidStr} onChange={(e) => setAmountPaidStr(e.target.value)} placeholder={defaultPaid.toFixed(2)} data-testid="manual-amount-paid" />
                <p className="text-[10px] text-white/40 mt-1">Laisse vide pour marquer comme <strong>payé intégralement</strong></p>
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Chambre attribuée (facultatif)</label>
                <Input value={adminRoomNote} onChange={(e) => setAdminRoomNote(e.target.value)} placeholder="Ex: Chambre 3 — Lavande" data-testid="manual-room-note" />
              </div>
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">Notes admin (facultatif)</label>
              <Textarea rows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Ex: Payé en 3 fois à la boutique le 15/03" data-testid="manual-admin-notes" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
            <Button
              onClick={submit}
              disabled={saving || !selected}
              className="bg-[#F5D8A2] text-black hover:bg-[#F5D8A2]/90"
              data-testid="manual-booking-submit"
            >
              {saving ? "Enregistrement…" : "Enregistrer la réservation"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// React import déjà présent en haut du fichier (ligne 7).

/**
 * Éditeur galerie : upload + réorganisation drag & drop + description par image.
 *
 * Chaque item est stocké au format {url, caption}. Le drag & drop utilise l'API HTML5
 * native (dataTransfer + dragover/drop) pour éviter d'ajouter une lib.
 * Le backend accepte à la fois `"url"` (legacy) et `{url, caption}`.
 */
function GalleryEditor({ items, onChange }) {
  // Normalise les items — supporte l'ancien format string
  const normalized = (items || []).map((it) => (typeof it === "string" ? { url: it, caption: "" } : it));
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const handleDragStart = (idx) => (e) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = "move";
    // Nécessaire sur Firefox
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* silent */ }
  };
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== idx) setDragOver(idx);
  };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null); setDragOver(null); return;
    }
    const next = [...normalized];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    onChange(next);
    setDragIndex(null); setDragOver(null);
  };
  const handleDragEnd = () => { setDragIndex(null); setDragOver(null); };

  const updateCaption = (idx, caption) => {
    const next = normalized.map((it, i) => (i === idx ? { ...it, caption } : it));
    onChange(next);
  };
  const removeItem = (idx) => {
    onChange(normalized.filter((_, i) => i !== idx));
  };

  return (
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
      {normalized.map((item, idx) => (
        <div
          key={idx}
          draggable
          onDragStart={handleDragStart(idx)}
          onDragOver={handleDragOver(idx)}
          onDrop={handleDrop(idx)}
          onDragEnd={handleDragEnd}
          className={`relative border rounded-lg overflow-hidden transition-all bg-white/5 ${
            dragIndex === idx ? "opacity-40 scale-95" : dragOver === idx ? "border-[#F5D8A2] scale-[1.02]" : "border-white/10"
          } cursor-move`}
          data-testid={`gallery-item-${idx}`}
        >
          <div className="aspect-square relative group">
            <img src={item.url} alt={item.caption || `Photo ${idx + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeItem(idx)}
              className="absolute top-1 right-1 bg-black/70 text-white p-1.5 rounded hover:bg-red-600/80 transition"
              aria-label="Retirer"
              data-testid={`gallery-remove-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-0.5 rounded pointer-events-none">
              #{idx + 1}
            </div>
          </div>
          <div className="p-2">
            <input
              type="text"
              value={item.caption || ""}
              onChange={(e) => updateCaption(idx, e.target.value)}
              placeholder="Description (facultatif)"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder-white/40 focus:outline-none focus:border-[#F5D8A2]/50"
              data-testid={`gallery-caption-${idx}`}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
            />
          </div>
        </div>
      ))}
      {/* Slot d'ajout — pas draggable */}
      <div className="aspect-square border border-dashed border-white/20 rounded-lg overflow-hidden">
        <ImageUploader
          currentUrl=""
          onUploaded={(url) => { if (url) onChange([...normalized, { url, caption: "" }]); }}
        />
      </div>
    </div>
  );
}
